import { createIsomorphicFn } from '@tanstack/react-start';
import { clientEnv } from '@/env';

// Trackers fire ONLY in production. Staging, development, and per-PR
// preview stages (`pr-123`) suppress every cookie tracker — landing,
// purchase, site-wide. Without this gate, every dev hit, every QA hit,
// and every PR-preview pageview pollutes production analytics with
// test traffic that gets counted as real user activity.
//
// `clientEnv.stage` is the canonical signal: it's set per-deployment
// from `STAGE` and is the only
// thing in the env that distinguishes production-the-stage from a
// production-NODE_ENV-built bundle running on staging. `isProdBuild`
// is NOT the right gate here — staging deploys are also `NODE_ENV=production`
// builds, so `isProdBuild` would be true on staging.
const isProductionStage = clientEnv.stage === 'production';

/**
 * Returns true when conversion-tracker pixels should be suppressed for
 * this request / page-load. Two independent reasons:
 *
 * 1. **Non-production stage.** Staging, dev, and PR previews never
 *    fire trackers; otherwise test traffic pollutes production
 *    analytics. This is the dominant gate — once you're in
 *    production, only the client-side reason remains.
 * 2. **Automation browsers** (`navigator.webdriver`). Headless
 *    Chrome / Playwright / Selenium identify themselves; their hits
 *    shouldn't appear as real conversions even on production
 *    (e.g. e2e canary runs against prod).
 *
 * The server branch MUST stay request-independent. Marketing HTML is
 * shared-cached (`s-maxage` at CloudFront) and the cache key does not —
 * and must not, see the cache-key warning in `packages/infra/src/web/cdn.ts` — include any
 * suppression signal, so a per-request server-side branch here would let
 * one request seed a pixel-free document into the shared cache for every
 * visitor. A request header that did exactly that
 * (`x-listening-disable-trackers`, set by the Playwright canaries) was
 * removed for this reason; on production the only automation guard is
 * `navigator.webdriver`, which lives client-side where caching can't
 * smear it across users.
 */
export const shouldDisableTrackers = createIsomorphicFn()
  .server(() => !isProductionStage)
  .client(() => {
    if (!isProductionStage) return true;
    return typeof navigator !== 'undefined' && navigator.webdriver;
  });
